/**
 * ReviewQueuePage — list of open and resolved review items.
 *
 * Filters: itemType, status, loanFileId. Every row links to
 * the detail page where an auditor can approve / correct / reject.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Filter } from 'lucide-react';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, Card, ErrorState, SectionLabel } from '@/acc-ui/primitives/Layout';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Button } from '@/acc-ui/primitives/Button';
import { DataTable, type Column } from '@/acc-ui/primitives/DataTable';
import { ThemedSelect } from '@/acc-ui/primitives/ThemedSelect';
import { parsingApi, type ReviewQueueQuery } from '@/api/parsing-api';
import { ingestionApi } from '@/api/ingestion-api';
import { REVIEW_ITEM_TYPE, REVIEW_STATUS, type ReviewQueueItemDTO } from '@loanaudit/shared';

export function ReviewQueuePage() {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<ReviewQueueQuery>(() => {
    const s = searchParams.get('status');
    return {
      page: Number(searchParams.get('page')) || 1,
      pageSize: 25,
      // Default to every status. A resolved review stays in the queue showing
      // Approved / Corrected / Rejected rather than vanishing, so the queue
      // reads as the loan file's review record and not just its backlog.
      status: (s || undefined) as ReviewQueueQuery['status'],
      itemType: (searchParams.get('itemType') || undefined) as ReviewQueueQuery['itemType'],
      loanFileId: searchParams.get('loanFileId') || undefined,
    };
  });

  // Sync filter state to the URL so refresh / deep-link / back preserve the view.
  useEffect(() => {
    const p = new URLSearchParams();
    p.set('status', filters.status ?? ''); // '' encodes "Any"
    if (filters.itemType) p.set('itemType', filters.itemType);
    if (filters.loanFileId) p.set('loanFileId', filters.loanFileId);
    if ((filters.page ?? 1) > 1) p.set('page', String(filters.page));
    setSearchParams(p, { replace: true });
  }, [filters, setSearchParams]);

  const query = useQuery({
    queryKey: ['parsing', 'reviewQueue', filters],
    queryFn: () => parsingApi.listReviewQueue(filters),
    refetchInterval: 10_000,
  });

  // Loan numbers for the dropdown — scope the queue to one loan file.
  const loanFilesQuery = useQuery({
    queryKey: ['reviewQueue', 'loanFileOptions'],
    queryFn: () => ingestionApi.list({ page: 1, pageSize: 200 }),
    staleTime: 60_000,
  });
  const loanOptions = [
    { value: '', label: 'All loan numbers' },
    ...(loanFilesQuery.data?.items ?? []).map((f) => ({
      value: f.id,
      label: f.loanNumber,
      description: f.borrowerName,
    })),
  ];

  return (
    <AppShell title="Review Queue">
      <div className="space-y-4">
        <PageHeader
          title="Review Queue"
          subtitle="Low-confidence items grouped by loan file, with their review outcome."
        />

        <Card>
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-text-muted" />
            <span className="font-semibold text-on-surface text-label">Filter</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <SectionLabel>Status</SectionLabel>
              <select
                className="mt-1 w-full border border-outline-variant rounded-lg px-3 py-2 text-label bg-surface-container-lowest outline-none focus:border-primary/50"
                value={filters.status ?? ''}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    page: 1,
                    status: (e.target.value || undefined) as ReviewQueueQuery['status'],
                  }))
                }
              >
                <option value="">Any</option>
                <option value={REVIEW_STATUS.PENDING}>Pending</option>
                <option value={REVIEW_STATUS.APPROVED}>Approved</option>
                <option value={REVIEW_STATUS.CORRECTED}>Corrected</option>
                <option value={REVIEW_STATUS.REJECTED}>Rejected</option>
              </select>
            </div>
            <div>
              <SectionLabel>Type</SectionLabel>
              <select
                className="mt-1 w-full border border-outline-variant rounded-lg px-3 py-2 text-label bg-surface-container-lowest outline-none focus:border-primary/50"
                value={filters.itemType ?? ''}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    page: 1,
                    itemType: (e.target.value || undefined) as ReviewQueueQuery['itemType'],
                  }))
                }
              >
                <option value="">Any</option>
                <option value={REVIEW_ITEM_TYPE.CLASSIFICATION}>Classification</option>
                <option value={REVIEW_ITEM_TYPE.EXTRACTION}>Extraction</option>
              </select>
            </div>
            <div>
              <SectionLabel>Loan number</SectionLabel>
              <div className="mt-1">
                <ThemedSelect
                  value={filters.loanFileId ?? ''}
                  onChange={(v) =>
                    setFilters((f) => ({ ...f, page: 1, loanFileId: v || undefined }))
                  }
                  placeholder="All loan numbers"
                  options={loanOptions}
                />
              </div>
            </div>
          </div>
        </Card>

        {query.isError ? (
          <ErrorState
            title="Failed to load review queue"
            description={query.error instanceof Error ? query.error.message : 'Unknown error'}
            action={
              <Button variant="secondary" onClick={() => query.refetch()}>
                Retry
              </Button>
            }
          />
        ) : (
          <DataTable<ReviewQueueItemDTO>
            columns={columns}
            rows={query.data?.items ?? []}
            rowKey={(i) => i.id}
            page={filters.page}
            pageSize={filters.pageSize}
            total={query.data?.total ?? 0}
            onPageChange={(p) => setFilters((f) => ({ ...f, page: p }))}
            loading={query.isLoading}
            onRowClick={(i) => navigate(`/parsing/review-queue/${i.id}`)}
            emptyMessage="No items match the current filters."
            groupBy={(i) => ({ key: i.loanFileId, label: i.loanNumber })}
          />
        )}
      </div>
    </AppShell>
  );
}

// Human-readable labels — underlying enum values stay for logic.
const TYPE_LABEL: Record<string, string> = {
  classification: 'Classification', extraction: 'Extraction',
};
const REVIEW_STATUS_LABEL: Record<string, string> = {
  pending: 'Pending', approved: 'Approved', corrected: 'Corrected', rejected: 'Rejected',
};

// Columns — same DataTable primitive as Loan Files, so the table chrome,
// font sizes, padding, borders, and pagination are identical across pages.
const columns: Column<ReviewQueueItemDTO>[] = [
  {
    key: 'itemType',
    header: 'Type',
    width: '8rem',
    cell: (i) => (
      <Badge tone={i.itemType === 'classification' ? 'info' : 'warning'}>{TYPE_LABEL[i.itemType] ?? i.itemType}</Badge>
    ),
  },
  {
    key: 'item',
    // The loan number is the group header, so the row only carries the document.
    header: 'Document',
    cell: (i) => <span className="block truncate text-text-primary">{i.documentFileName}</span>,
  },
  {
    key: 'documentType',
    header: 'Doc type',
    width: '10rem',
    cell: (i) =>
      i.documentType ? <Badge tone="muted">{i.documentType}</Badge> : <span className="text-text-muted">—</span>,
  },
  {
    key: 'confidence',
    header: 'Confidence',
    width: '8rem',
    cell: (i) => <ConfidencePill value={i.confidence} />,
  },
  {
    key: 'status',
    header: 'Status',
    width: '8rem',
    cell: (i) => <StatusPill status={i.status} />,
  },
  {
    key: 'createdAt',
    header: 'Created',
    width: '12rem',
    cell: (i) => (
      <span className="text-text-tertiary tabular-nums">
        {new Date(i.createdAt).toLocaleString()}
      </span>
    ),
  },
];

function ConfidencePill({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const tone: 'success' | 'warning' | 'danger' =
    value >= 0.85 ? 'success' : value >= 0.65 ? 'warning' : 'danger';
  return <Badge tone={tone}>{pct}%</Badge>;
}

function StatusPill({ status }: { status: string }) {
  const tone: 'muted' | 'info' | 'warning' | 'success' | 'danger' =
    status === 'pending' ? 'warning'
      : status === 'approved' ? 'success'
      : status === 'corrected' ? 'info'
      : status === 'rejected' ? 'danger'
      : 'muted';
  return <Badge tone={tone}>{REVIEW_STATUS_LABEL[status] ?? status}</Badge>;
}
